import { Box } from "./box";
import { Skeleton, useDelayedFade, variedLength } from "./skeleton";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type TabsTabSkeletonProps = Omittable<{
  /** Label bar width in px. */
  labelWidth?: number;
}>;

/**
 * One `underline`-genre tab standing in for a tab whose identity has not
 * loaded: the 14px compact identity tile and a label-length bar, on the
 * tab's own 40px box with its 4px/8px insets, so the real tab lands on the
 * same pixels. Not a `TabsTab` — it has no value, cannot be pressed and
 * takes no part in the list's order or overflow measurement.
 */
export function TabsTabSkeleton({ labelWidth = 56 }: TabsTabSkeletonProps) {
  return (
    <Box
      direction="row"
      align="center"
      height={40}
      className="gap-1.5 ps-1 pe-2"
      data-slot="tabs-tab-skeleton"
    >
      <Skeleton width={14} height={14} className="rounded-[3px]" />
      <Skeleton width={labelWidth} height={12} />
    </Box>
  );
}

export type TabsStripSkeletonProps = Omittable<{
  /** How many tab stand-ins to lay. Defaults to three — a plausible strip,
   *  not a count the data has promised. */
  tabs?: number;
  /** Invisible for its first ~200ms, then fades in as one, so a strip whose
   *  rows arrive from cache never shows a stand-in. */
  delayed?: boolean;
}>;

/**
 * An `underline` strip whose tabs are still loading: `tabs` stand-ins in the
 * list's own 4px rhythm, in place of the `Tabs`/`TabsList` pair. The strip's
 * static chrome beside it (a rail separator, an add action) renders real —
 * only the tabs are unknown.
 */
export function TabsStripSkeleton({ tabs = 3, delayed = false }: TabsStripSkeletonProps) {
  const fade = useDelayedFade(delayed);
  return (
    <Box
      direction="row"
      align="center"
      className={cn("gap-x-1", fade)}
      data-slot="tabs-strip-skeleton"
      aria-hidden
    >
      {Array.from({ length: Math.max(0, tabs) }, (_, index) => (
        <TabsTabSkeleton
          key={index}
          labelWidth={Number(variedLength(56, index))}
        />
      ))}
    </Box>
  );
}
